/**
 * CSRF Token Utilities
 * 
 * Client-side utilities for CSRF protection
 */

/** The body proxy.ts answers (403) when the CSRF check refuses a request before any route ran. */
export const CSRF_REFUSAL_MESSAGE = 'Invalid CSRF token. Please refresh the page and try again.';

const CSRF_PROTECTED_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

/**
 * Get CSRF token from cookie
 */
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrf-token='));
  
  return cookie ? cookie.split('=')[1] : null;
}

/**
 * Add CSRF token to fetch headers
 */
export function addCsrfHeader(headers: HeadersInit = {}): HeadersInit {
  const token = getCsrfToken();
  
  if (!token) {
    return headers;
  }
  
  return {
    ...headers,
    'X-CSRF-Token': token,
  };
}

/**
 * Fetch wrapper with automatic CSRF token
 */
export async function fetchWithCsrf(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const method = options.method?.toUpperCase() || 'GET';
  const needsCsrf = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
  
  if (needsCsrf) {
    options.headers = addCsrfHeader(options.headers);
  }
  
  return fetch(url, options);
}

/**
 * React hook for CSRF token
 */
export function useCsrfToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  return getCsrfToken();
}

function sentToken(init?: RequestInit): string | null {
  return init?.headers ? new Headers(init.headers).get('x-csrf-token') : null;
}

/** A same-origin, state-changing /api/ call whose body can be sent a second time. */
function isReplayable(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (typeof input !== 'string' && !(input instanceof URL)) return false; // a Request's body is single-use
  const method = (init?.method || 'GET').toUpperCase();
  if (!CSRF_PROTECTED_METHODS.includes(method)) return false;
  if (typeof ReadableStream !== 'undefined' && init?.body instanceof ReadableStream) return false;
  try {
    const url = new URL(String(input), window.location.href);
    return url.origin === window.location.origin && url.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/**
 * Retry a CSRF refusal once, so the customer never sees "refresh the page".
 *
 * proxy.ts refuses before any route runs, so nothing happened and sending the
 * request again cannot repeat an action. The refusal also re-issues a good cookie
 * when the old one was unsigned or badly signed (a secret rotation, the end of the
 * legacy window), or another tab rotated it. The retry reads the cookie again and
 * goes only if it now holds a token other than the one sent: the same token would
 * be refused the same way. Once, never in a loop. Idempotent; wraps window.fetch
 * for every caller, so no call site changes.
 */
export function installCsrfRetry(): void {
  if (typeof window === 'undefined') return;
  const marked = window as Window & { __csrfRetryInstalled?: boolean };
  if (marked.__csrfRetryInstalled) return;
  marked.__csrfRetryInstalled = true;

  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await original(input, init);
    if (response.status !== 403 || !isReplayable(input, init)) return response;

    const refused = await response
      .clone()
      .json()
      .then((body) => body?.error === CSRF_REFUSAL_MESSAGE, () => false);
    if (!refused) return response;

    const fresh = getCsrfToken();
    if (!fresh || fresh === sentToken(init)) return response;

    const headers = new Headers(init?.headers);
    headers.set('X-CSRF-Token', fresh);
    return original(input, { ...init, headers });
  };
}
